import { TIERS } from "../data/pricing";

// Per-route SEO metadata + schema.org JSON-LD.
//
// Used in two places:
//   - scripts/prerender.mjs (via entry-server) writes these tags into the
//     static HTML for each prerendered route, so crawlers that do not run
//     JavaScript (most AI search crawlers) see them.
//   - useRouteMeta() keeps document.head in sync during client-side
//     navigation between routes.
//
// Rule: no em dashes in any of this copy.

export const SITE_URL = "https://agent-booking.com";
export const SITE_NAME = "Dash";
export const LOGO_URL = `${SITE_URL}/assets/logo.svg`;

export type RouteMeta = {
  path: string;
  title: string;
  description: string;
};

export const ROUTE_META: Record<string, RouteMeta> = {
  "/": {
    path: "/",
    title: "AI Phone Receptionist That Books Your Calendar | Dash",
    description:
      "AI phone receptionist for trades, contractors, and small businesses. Dash answers every call, checks your real calendar, books the job, and emails you who / what / when. Book a demo.",
  },
  "/pricing": {
    path: "/pricing",
    title: "Dash Pricing: AI Receptionist from $49/mo | Book a Demo",
    description:
      "Dash AI phone receptionist pricing: Starter $49/mo, Business $149/mo, Bespoke from $399/mo. Flat monthly, no per-minute charges. Book a demo to see it on your line.",
  },
};

/** Routes that are prerendered to static HTML at build time. */
export const PRERENDER_ROUTES = Object.keys(ROUTE_META);

export function canonicalUrl(path: string): string {
  return path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const APP_ID = `${SITE_URL}/#software`;

const APP_DESCRIPTION =
  "Dash is an AI phone receptionist for trades, contractors, and small businesses. It answers your business phone on the first ring, checks your real calendar, books the job, and emails you a summary of who called, what they need, and when. Flat monthly pricing with no per-minute charges.";

function offers() {
  return TIERS.map((t) => ({
    "@type": "Offer",
    name: `Dash ${t.name}`,
    description: t.startingAt
      ? `${t.summary} Starting at $${t.monthlyUsd} per month, flat monthly, no per-minute charges.`
      : `${t.summary} $${t.monthlyUsd} per month, flat monthly, no per-minute charges.`,
    price: String(t.monthlyUsd),
    priceCurrency: "USD",
    url: canonicalUrl("/pricing"),
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: String(t.monthlyUsd),
      ...(t.startingAt ? { minPrice: String(t.monthlyUsd) } : {}),
      priceCurrency: "USD",
      unitCode: "MON",
      unitText: "month",
      billingDuration: "P1M",
    },
  }));
}

export function jsonLdFor(path: string): Record<string, unknown> {
  const meta = ROUTE_META[path] ?? ROUTE_META["/"];
  const url = canonicalUrl(meta.path);

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": ORG_ID,
        name: SITE_NAME,
        url: `${SITE_URL}/`,
        logo: LOGO_URL,
        description:
          "Dash makes an AI phone receptionist that answers calls and books appointments for trades and small businesses.",
      },
      {
        "@type": "WebSite",
        "@id": WEBSITE_ID,
        name: SITE_NAME,
        url: `${SITE_URL}/`,
        publisher: { "@id": ORG_ID },
        inLanguage: "en-US",
      },
      {
        "@type": "SoftwareApplication",
        "@id": APP_ID,
        name: "Dash AI Phone Receptionist",
        alternateName: "Dash",
        applicationCategory: "BusinessApplication",
        applicationSubCategory: "AI receptionist and appointment booking",
        operatingSystem: "Web",
        url: `${SITE_URL}/`,
        description: APP_DESCRIPTION,
        publisher: { "@id": ORG_ID },
        audience: {
          "@type": "BusinessAudience",
          audienceType: "Trades, contractors, and small service businesses",
        },
        featureList: [
          "Answers business calls on the first ring, any hour",
          "Books appointments on your real Cal.com calendar",
          "Emails a call summary: who called, what they want, and when",
          "Keeps transcripts, recordings, and caller ID",
          "Works with your existing phone number via call forwarding",
        ],
        offers: offers(),
      },
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: meta.title,
        description: meta.description,
        isPartOf: { "@id": WEBSITE_ID },
        about: { "@id": APP_ID },
        inLanguage: "en-US",
      },
    ],
  };
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Static <head> tags for a prerendered route. */
export function headTagsFor(path: string): string {
  const meta = ROUTE_META[path] ?? ROUTE_META["/"];
  const url = canonicalUrl(meta.path);
  const title = escapeAttr(meta.title);
  const desc = escapeAttr(meta.description);
  // JSON inside <script> only needs "<" escaped to avoid closing the tag.
  const ld = JSON.stringify(jsonLdFor(meta.path)).replace(/</g, "\\u003c");

  return [
    `<title>${title}</title>`,
    `<meta name="description" content="${desc}" />`,
    `<link rel="canonical" href="${url}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="${SITE_NAME}" />`,
    `<meta property="og:locale" content="en_US" />`,
    `<meta property="og:title" content="${title}" />`,
    `<meta property="og:description" content="${desc}" />`,
    `<meta property="og:url" content="${url}" />`,
    `<meta name="twitter:card" content="summary" />`,
    `<meta name="twitter:title" content="${title}" />`,
    `<meta name="twitter:description" content="${desc}" />`,
    `<script type="application/ld+json">${ld}</script>`,
  ].join("\n    ");
}

/**
 * Head tags for the non-prerendered SPA fallback shell (any path that is not
 * a prerendered route). No canonical or og:url, since the shell is served at
 * arbitrary URLs.
 */
export function shellHeadTagsFor(): string {
  const meta = ROUTE_META["/"];
  const title = escapeAttr(meta.title);
  const desc = escapeAttr(meta.description);
  return [
    `<title>${title}</title>`,
    `<meta name="description" content="${desc}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="${SITE_NAME}" />`,
    `<meta property="og:title" content="${title}" />`,
    `<meta property="og:description" content="${desc}" />`,
    `<meta name="twitter:card" content="summary" />`,
  ].join("\n    ");
}
